import axios from "axios";

// Shared client-side cache for interview records.
// Uses both in-memory cache (per tab) and sessionStorage (per token) with a TTL.

const INTERVIEWS_CACHE_KEY_PREFIX = "candidate_interviews_";
// REDUCED: 5 minutes to prevent stale data issues (was 30 minutes)
const INTERVIEWS_CACHE_TTL = 5 * 60 * 1000; // 5 minutes

// inMemoryCache is explicitly tied to the token used to fetch it
// to avoid leaking data across logins in the same tab.
let inMemoryCache = null; // { data, timestamp, token }
let inFlightPromise = null;

/**
 * Get all interviews for the current candidate, with aggressive client-side caching.
 *
 * - Reuses in-memory cache while fresh.
 * - Falls back to sessionStorage cache if available and fresh.
 * - Otherwise calls /api/interviews/fetchInterviews once per tab (shares inFlightPromise).
 *
 * @param {Object} options
 * @param {boolean} options.forceRefresh - bypasses caches and refetches from the API.
 * @returns {Promise<Array>} Array of Airtable interview records (raw from API).
 */
export async function getInterviews({ forceRefresh = false } = {}) {
  const token = typeof window !== "undefined" ? localStorage.getItem("token") : null;
  if (!token) {
    return [];
  }

  const cacheKey = `${INTERVIEWS_CACHE_KEY_PREFIX}${token.slice(0, 32)}`;
  const now = Date.now();

  // If not forcing, try in-memory cache first
  if (
    !forceRefresh &&
    inMemoryCache &&
    inMemoryCache.token === token &&
    now - inMemoryCache.timestamp < INTERVIEWS_CACHE_TTL
  ) {
    return inMemoryCache.data;
  }

  // Then try sessionStorage cache
  if (!forceRefresh) {
    try {
      const cached = sessionStorage.getItem(cacheKey);
      if (cached) {
        const parsed = JSON.parse(cached);
        if (parsed && Array.isArray(parsed.data) && now - parsed.timestamp < INTERVIEWS_CACHE_TTL) {
          inMemoryCache = { data: parsed.data, timestamp: parsed.timestamp, token };
          return parsed.data;
        }
      }
    } catch (e) {
      // If parsing fails, clear the bad cache entry
      try {
        sessionStorage.removeItem(cacheKey);
      } catch (_) {}
    }
  }

  // If a fetch is already in-flight and we're not forcing, share it
  if (!forceRefresh && inFlightPromise) {
    return inFlightPromise;
  }

  const doFetch = async () => {
    const response = await axios.get("/api/interviews/fetchInterviews", {
      headers: { Authorization: `Bearer ${token}` },
    });

    const interviews = response.data?.interviews || [];
    const payload = { data: interviews, timestamp: Date.now(), token };

    inMemoryCache = payload;
    try {
      sessionStorage.setItem(cacheKey, JSON.stringify(payload));
    } catch (_) {
      // Ignore storage quota errors
    }

    return interviews;
  };

  if (forceRefresh) {
    // Clear caches and fetch fresh
    clearInterviewsCache();
    return doFetch();
  }

  inFlightPromise = doFetch()
    .catch((err) => {
      // On error, don't keep a bad cache around
      inMemoryCache = null;
      throw err;
    })
    .finally(() => {
      inFlightPromise = null;
    });

  return inFlightPromise;
}

/**
 * Clear client-side interview caches (in-memory + sessionStorage).
 * Useful after actions that change interview records (accept/decline, status updates, etc.).
 */
export function clearInterviewsCache() {
  console.log("🧹 [interviewsClient] clearInterviewsCache called");
  const token = typeof window !== "undefined" ? localStorage.getItem("token") : null;
  if (token) {
    const cacheKey = `${INTERVIEWS_CACHE_KEY_PREFIX}${token.slice(0, 32)}`;
    console.log("🧹 [interviewsClient] Removing sessionStorage cache:", cacheKey);
    try {
      sessionStorage.removeItem(cacheKey);
    } catch (_) {}
  }
  inMemoryCache = null;
  console.log("🧹 [interviewsClient] In-memory cache cleared");
}


